import { tryCatch } from "~/utils/gw-utils";
import { AddonBase } from "./addon-base";

/**
 * Advanced Addon Details Modal Component
 * Manages modal display with caching, accessibility, error handling, and performance optimizations
 */
export class AddonDetails extends AddonBase {
  // Configuration constants
  static CONFIG = {
    TEMPLATE_NAMES: {
      CTA: 'addon-details-cta',
    },
    SELECTORS: {
      CTA_SLOT: '[data-slot="addon-details-cta"]',
      SECTION_ID: 'shopify-section-addon-details',
      MODAL_CLOSE: '.addon-modal__close',
      MODAL_CONTENT: '.addon-modal__content',
      MODAL_DETAILS: '.addon-modal__details',
    },
    CSS_CLASSES: {
      MODAL: 'addon-modal',
      MODAL_OPEN: 'addon-modal--open',
      MODAL_LOADING: 'addon-modal--loading',
      MODAL_ERROR: 'addon-modal--error'
    },
    EVENTS: {
      MODAL_OPEN: 'addon-details:modal-open',
      MODAL_CLOSE: 'addon-details:modal-close',
      MODAL_ERROR: 'addon-details:modal-error',
      CONTENT_LOADED: 'addon-details:content-loaded'
    },
    TIMEOUTS: {
      MODAL_SIZING: 300,
      ERROR_CLEAR: 5000
    }
  };

  // Static cache for fetched content
  static contentCache = new Map();

  constructor() {
    super();

    this._handleTriggerClick = this._handleTriggerClick.bind(this);
    this._handleModalClick = this._handleModalClick.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);

    this._focusableElements = [];
    this._lastFocusedElement = null;
  }

  static get observedAttributes() {
    return ['product-handle'];
  }

  _getInitialState() {
    return {
      isOpen: false,
      isLoading: false,
      error: null,
      contentLoaded: false
    };
  }

  /**
   * Validate required attributes
   * @protected
   */
  _validateAttributes() {
    const productHandle = this.getAttribute('product-handle');
    if (!productHandle) {
      throw new Error('AddonDetails: product-handle attribute is required');
    }

    this._productHandle = productHandle;
  }

  /**
   * Cache frequently accessed DOM elements
   * @protected
   */
  _cacheDOMReferences() {
    const trigger = this.querySelector('button');
    if (!trigger) {
      throw new Error('AddonDetails: button element not found');
    }

    this._domRefs.set('trigger', trigger);

    this._cacheTemplates();
  }

  /**
   * Cache template elements
   * @private
   */
  _cacheTemplates() {
    const { TEMPLATE_NAMES } = AddonDetails.CONFIG;

    this._domRefs.set('ctaTemplate', this.querySelector(`template[name="${TEMPLATE_NAMES.CTA}"]`));
  }

  /**
   * Setup event listeners with proper cleanup tracking
   * @protected
   */
  _setupEventListeners() {
    const trigger = this._domRefs.get('trigger');

    this._addEventListener(trigger, 'click', this._handleTriggerClick);
  }

  /**
   * Setup accessibility attributes
   * @protected
   */
  _setupAccessibility() {
    const trigger = this._domRefs.get('trigger');

    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');

    if (!trigger.hasAttribute('aria-label')) {
      trigger.setAttribute('aria-label', 'View addon details');
    }
  }

  /**
   * Handle trigger button click
   * @protected
   */
  async _handleTriggerClick(event) {
    event.preventDefault();

    if (this._state.isLoading) {
      return;
    }

    try {
      await this._openModal();
    } catch (error) {
      this._handleError('Failed to open modal', error);
    }
  }

  /**
   * Handle attribute changes
   * @protected
   */
  _handleAttributeChange(name, newValue) {
    switch (name) {
      case 'product-handle':
        this._productHandle = newValue;
        // Clear cache for this product
        AddonDetails.contentCache.delete(this._getCacheKey());
        break;
    }
  }

  /**
   * Open modal with content loading and error handling
   * @private
   */
  async _openModal() {
    this._updateState({ isLoading: true, error: null });

    this._lastFocusedElement = document.activeElement;

    try {
      const content = await this._fetchSectionContent();
      if (!content) {
        throw new Error('Failed to load addon details content');
      }

      await this._createAndSetupModal(content);

      this._updateState({
        isOpen: true,
        isLoading: false,
        contentLoaded: true
      });

      this._dispatchEvent(AddonDetails.CONFIG.EVENTS.MODAL_OPEN, {
        productHandle: this._productHandle,
        component: this
      });

    } catch (error) {
      this._updateState({ isLoading: false });
      throw error;
    }
  }

  /**
   * Create and setup modal with accessibility
   * @private
   */
  async _createAndSetupModal(content) {
    const modal = this._createModalElement();
    this._domRefs.set('modal', modal);

    await this._setupModalContent(modal, content);

    this._setupModalEvents(modal);
    this._setupModalAccessibility(modal);

    document.body.appendChild(modal);

    modal.offsetHeight;
    modal.classList.add(AddonDetails.CONFIG.CSS_CLASSES.MODAL_OPEN);

    document.body.style.setProperty('overflow', 'hidden');

    this._setInitialFocus(modal);
  }

  /**
   * Create modal DOM element
   * @private
   */
  _createModalElement() {
    const modal = document.createElement('div');
    modal.classList.add(AddonDetails.CONFIG.CSS_CLASSES.MODAL);
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'addon-modal-title');

    modal.innerHTML = `
      <div class="addon-modal__container">
        <div class="addon-modal__content">
          <div class="addon-modal__header">
            <button class="addon-modal__close button-unstyled" data-addon-modal-close aria-label="Close addon details" type="button">
              <svg width="34" height="34" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M38.1844 12.7278L12.7285 38.1836" stroke="#262626" stroke-width="1.80738" stroke-linecap="square"/>
                <path d="M38.1844 38.1844L12.7285 12.7285" stroke="#262626" stroke-width="1.80738" stroke-linecap="square"/>
              </svg>
            </button>
          </div>
          <div class="addon-modal__details" role="document"></div>
        </div>
      </div>
    `;

    return modal;
  }

  /**
   * Setup modal content with templates
   * @private
   */
  async _setupModalContent(modal, content) {
    const detailsContainer = modal.querySelector(AddonDetails.CONFIG.SELECTORS.MODAL_DETAILS);
    const addonSection = content.getElementById(AddonDetails.CONFIG.SELECTORS.SECTION_ID);

    if (!addonSection) {
      throw new Error('Addon details section not found in fetched content');
    }

    const sectionClone = addonSection.cloneNode(true);

    this._insertTemplatesIntoSlots(sectionClone);

    const fragment = document.createDocumentFragment();
    while (sectionClone.firstChild) {
      fragment.appendChild(sectionClone.firstChild);
    }

    detailsContainer.appendChild(fragment);
  }

  /**
   * Insert templates into designated slots
   * @private
   */
  _insertTemplatesIntoSlots(sectionElement) {
    const { SELECTORS } = AddonDetails.CONFIG;

    const ctaSlot = sectionElement.querySelector(SELECTORS.CTA_SLOT);

    if (!ctaSlot) {
      console.warn('AddonDetails: CTA slots not found in content');
      return;
    }

    const ctaTemplate = this._domRefs.get('ctaTemplate');

    if (ctaTemplate) {
      this._insertTemplateContent(ctaSlot, ctaTemplate);
    }
  }

  /**
   * Insert template content efficiently
   * @private
   */
  _insertTemplateContent(slot, template) {
    if (template && template.content) {
      const fragment = template.content.cloneNode(true);
      slot.appendChild(fragment);
    }
  }

  /**
   * Setup modal event listeners
   * @private
   */
  _setupModalEvents(modal) {
    const closeButton = modal.querySelector(AddonDetails.CONFIG.SELECTORS.MODAL_CLOSE);

    this._addEventListener(closeButton, 'click', this._handleModalClick);
    this._addEventListener(document, 'keydown', this._handleKeyDown);
  }

  /**
   * Setup modal accessibility features
   * @private
   */
  _setupModalAccessibility(modal) {
    this._cacheFocusableElements(modal);

    const trigger = this._domRefs.get('trigger');
    trigger.setAttribute('aria-expanded', 'true');
  }

  /**
   * Cache focusable elements for focus management
   * @private
   */
  _cacheFocusableElements(modal) {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this._focusableElements = Array.from(modal.querySelectorAll(focusableSelectors));
  }

  /**
   * Set initial focus in modal
   * @private
   */
  _setInitialFocus(modal) {
    const closeButton = modal.querySelector(AddonDetails.CONFIG.SELECTORS.MODAL_CLOSE);
    if (closeButton) {
      closeButton.focus();
    }
  }

  /**
   * Handle modal clicks (close and backdrop)
   * @private
   */
  _handleModalClick(event) {
    console.log('handleModalClick', event);

    const modal = this._domRefs.get('modal');

    if (event.target === modal || event.target.closest(AddonDetails.CONFIG.SELECTORS.MODAL_CLOSE)) {
      event.preventDefault();
      this._attemptCloseModal();
    }
  }

  /**
   * Handle keyboard events for accessibility
   * @private
   */
  _handleKeyDown(event) {
    if (!this._state.isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this._attemptCloseModal();
        break;
      case 'Tab':
        this._handleTabKey(event);
        break;
    }
  }

  /**
   * Handle tab key for focus trap
   * @private
   */
  _handleTabKey(event) {
    if (this._focusableElements.length === 0) return;

    const firstElement = this._focusableElements[0];
    const lastElement = this._focusableElements[this._focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Attempt to close modal - now closes immediately since cart operations are synchronous
   * @private
   */
  _attemptCloseModal() {
    console.log('attemptCloseModal');

    // Close immediately since we no longer have deferred operations
    this._closeModal();
  }

  /**
   * Close modal with cleanup
   * @private
   */
  _closeModal() {
    const modal = this._domRefs.get('modal');
    if (!modal) return;

    modal.classList.remove(AddonDetails.CONFIG.CSS_CLASSES.MODAL_OPEN);

    setTimeout(() => {
      this._cleanupModal();
    }, 300); // Match CSS transition duration

    this._updateState({ isOpen: false });

    this._dispatchEvent(AddonDetails.CONFIG.EVENTS.MODAL_CLOSE, {
      productHandle: this._productHandle,
      component: this
    });
  }

  /**
   * Clean up modal and restore state
   * @private
   */
  _cleanupModal() {
    const modal = this._domRefs.get('modal');

    if (modal) {
      modal.remove();
      this._domRefs.delete('modal');
    }

    document.body.style.removeProperty('overflow');

    if (this._lastFocusedElement) {
      this._lastFocusedElement.focus();
      this._lastFocusedElement = null;
    }

    const trigger = this._domRefs.get('trigger');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this._cleanupModalEvents();

    this._focusableElements = [];
  }

  /**
   * Clean up modal-specific event listeners
   * @private
   */
  _cleanupModalEvents() {
    if (this._modalEventListeners) {
      this._modalEventListeners.forEach(({ element, event, handler }) => {
        element.removeEventListener(event, handler);
      });
      this._modalEventListeners = null;
    }
  }

  /**
   * Clean up modal-specific event listeners
   * @protected
   */
  _performChildCleanup() {
    this._cleanupModalEvents();
    this._cleanupModal();
  }

  /**
   * Fetch section content with caching
   * @private
   */
  async _fetchSectionContent() {
    const cacheKey = this._getCacheKey();

    if (AddonDetails.contentCache.has(cacheKey)) {
      return AddonDetails.contentCache.get(cacheKey);
    }

    try {
      const url = `/products/${this._productHandle}?sections=addon-details`;
      const { data, error } = await tryCatch(
        fetch(url).then(response => response.json())
      );

      if (error) {
        throw new Error(`Failed to fetch addon details: ${error.message}`);
      }

      if (!data || !data['addon-details']) {
        throw new Error('Addon details section not found in response');
      }

      const section = data['addon-details'];
      const html = new DOMParser().parseFromString(section, 'text/html');

      AddonDetails.contentCache.set(cacheKey, html);

      return html;

    } catch (error) {
      console.error('AddonDetails: Error fetching section content', error);
      throw error;
    }
  }

  /**
   * Generate cache key for content
   * @private
   */
  _getCacheKey() {
    return `addon-details-${this._productHandle}`;
  }


  /**
   * Render component based on current state
   * @private
   */
  _render() {
    this._updateAccessibility();
  }

  /**
   * Update accessibility attributes based on state
   * @protected
   */
  _updateAccessibility() {
    const trigger = this._domRefs.get('trigger');
    if (!trigger) return;

    trigger.setAttribute('aria-expanded', this._state.isOpen.toString());

    if (this._state.isLoading) {
      trigger.setAttribute('aria-busy', 'true');
    } else {
      trigger.removeAttribute('aria-busy');
    }
  }

  /**
   * Get modal open state
   */
  get isOpen() {
    return this._state?.isOpen || false;
  }

  /**
   * Get loading state
   */
  get isLoading() {
    return this._state?.isLoading || false;
  }

  /**
   * Programmatically open modal
   */
  async openModal() {
    if (!this._state.isOpen && !this._state.isLoading) {
      return this._openModal();
    }
  }

  /**
   * Programmatically close modal
   */
  closeModal() {
    if (this._state.isOpen) {
      this._closeModal();
    }
  }

  /**
   * Clear content cache
   */
  static clearCache(productHandle = null) {
    if (productHandle) {
      const cacheKey = `addon-details-${productHandle}`;
      AddonDetails.contentCache.delete(cacheKey);
    } else {
      AddonDetails.contentCache.clear();
    }
  }

  /**
   * Refresh content (clears cache and refetches)
   */
  async refresh() {
    AddonDetails.clearCache(this._productHandle);

    if (this._state.isOpen) {
      this._closeModal();
      setTimeout(() => this._openModal(), 350);
    }
  }
}